import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { XMarkIcon, BuildingStorefrontIcon } from '@heroicons/react/24/outline';

export interface BrandOnlyNoticeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Explains that a brand-only feature is out of reach, instead of navigating to
 * it and rendering a rejection page. Shown when a signed-in user without host
 * access activates a brand CTA — the navigation is cancelled and this appears
 * in place of it, so the user keeps their position on the page.
 *
 * The route guard stays in place regardless; this only improves the path
 * through the UI, not the enforcement.
 */
const BrandOnlyNoticeModal: React.FC<BrandOnlyNoticeModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation('common');

  // Escape closes, as expected of a dialog. Bound only while open so the
  // listener is not left attached for the lifetime of the page.
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#0F1E3D]/40 p-5"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="brand-only-notice-title"
      aria-describedby="brand-only-notice-body"
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-[420px] text-left"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-6 pt-5">
          <div className="flex items-center gap-2.5">
            <span className="w-9 h-9 rounded-full bg-[#E6EDFF] text-[#2D5BFF] flex items-center justify-center flex-shrink-0">
              <BuildingStorefrontIcon className="w-5 h-5" aria-hidden="true" />
            </span>
            <h2
              id="brand-only-notice-title"
              className="text-[16.5px] font-bold text-[#0F1E3D] tracking-tight"
            >
              {t('brandOnlyNotice.title', '브랜드 회원 전용 기능입니다')}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-7 h-7 rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600 flex items-center justify-center flex-shrink-0"
            aria-label={t('close', '닫기')}
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div id="brand-only-notice-body" className="px-6 pt-4 pb-1">
          <p className="text-sm leading-relaxed text-[#3D4A63]">
            {t(
              'brandOnlyNotice.body',
              '캠페인 등록은 브랜드(광고주) 회원만 이용할 수 있어요. 현재 로그인된 계정에는 캠페인 등록 권한이 없습니다.',
            )}
          </p>
          <p className="mt-3 text-sm leading-relaxed text-[#6B7794]">
            {t(
              'brandOnlyNotice.hint',
              '인플루언서 회원은 아래에서 진행 중인 캠페인을 둘러보고 지원할 수 있어요.',
            )}
          </p>
        </div>

        {/* Footer */}
        <div className="flex justify-end px-6 py-5">
          <button
            type="button"
            onClick={onClose}
            autoFocus
            className="px-5 py-2.5 text-sm font-semibold text-white bg-[#0F1E3D] rounded-lg hover:bg-[#1B2E52] transition-colors"
          >
            {t('confirm', '확인')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BrandOnlyNoticeModal;
